import { useEffect, useState } from "react";
import { motion, AnimatePresence, MotionProvider } from "./motion";
import { Sparkles, ArrowRight, Menu, X } from "lucide-react";
import { Logo, CTA, scrollToId } from "./ui";
import { getAppUrl } from "@/urls";
import { Hero } from "./Hero";
import { Waitlist } from "./Waitlist";
import { Coaching, Steps, Features, Examples, Emotion, Demo, Faq, FinalCta } from "./Sections";

const NAV = [
  { label: "How it works", id: "how" },
  { label: "Features", id: "features" },
  { label: "Examples", id: "examples" },
  { label: "FAQ", id: "faq" },
];

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 24);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  const go = (id) => { setMenu(false); scrollToId(id); };
  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-[#0D0D0F]/80 border-b border-[#2A2A35]" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 md:h-18 flex items-center justify-between">
        <a href="#top" onClick={(e) => { e.preventDefault(); window.scrollTo({ top: 0, behavior: "smooth" }); }} data-testid="nav-logo" className="inline-flex items-center py-1.5 pr-2 bg-transparent">
          <Logo className="h-7 md:h-8" />
        </a>
        <nav className="hidden md:flex items-center gap-8">
          {NAV.map((n) => (
            <button key={n.id} onClick={() => go(n.id)} data-testid={`nav-${n.id}`}
              className="text-sm font-semibold tracking-wide text-[#C9C9D4] hover:text-[#C9A84C] transition-colors">
              {n.label}
            </button>
          ))}
        </nav>
        {/* Batch 136 — a second control here said the name of a list that no
            longer exists, beside a control that opens the app. One job, one
            control. The same entry went from the mobile menu below. */}
        <div className="hidden md:flex items-center gap-3">
          <CTA href={getAppUrl()} testid="nav-start-free" className="!px-5 !py-2.5 !text-sm">Start Free</CTA>
        </div>
        <button className="md:hidden text-[#F0F0F0]" onClick={() => setMenu((m) => !m)} aria-label="Menu" data-testid="nav-menu-toggle">
          {menu ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>
      <AnimatePresence>
        {menu && (
          <motion.div
            initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
            className="md:hidden overflow-hidden backdrop-blur-xl bg-[#0D0D0F]/95 border-b border-[#2A2A35]"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {NAV.map((n) => (
                <button key={n.id} onClick={() => go(n.id)} className="text-left py-2.5 text-[#C9C9D4] font-medium">{n.label}</button>
              ))}
              <CTA href={getAppUrl()} className="mt-2 w-full">Start Free <ArrowRight size={16} /></CTA>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

const StickyMobileCta = () => {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const on = () => setShow(window.scrollY > 700);
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ y: 80, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 80, opacity: 0 }}
          className="md:hidden fixed bottom-0 inset-x-0 z-40 p-3 backdrop-blur-xl bg-[#0D0D0F]/85 border-t border-[#2A2A35] flex gap-2"
        >
          {/* Batch 136 — there were two here, "Start Free" and "Start free",
              side by side, one word apart, going to different places. One of
              them scrolled to a form that promised an invite to a product the
              other one opened directly. */}
          <CTA href={getAppUrl()} testid="sticky-start-free" className="flex-1 !py-3">Start Free</CTA>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Footer = () => (
  <footer className="border-t border-[#2A2A35] bg-[#0D0D0F]">
    <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12 flex flex-col md:flex-row items-center justify-between gap-6">
      <Logo className="h-7" />
      <p className="text-[#7A7A8C] text-sm font-['Inter'] text-center">
        © {new Date().getFullYear()} SparkCue. A coach that works with you.
      </p>
      {/* Batch 134 — "Privacy" used to be a button that scrolled to the FAQ.
          A control labelled for a document, going somewhere else, reads as a
          policy existing when it did not, which is worse than no link at all.
          Both are now real navigations. Neither label may contain the word
          Beta: publicSignup.test.js greps this whole FILE for a tag-wrapped
          occurrence of it, and the helper it uses only strips comment lines
          that START with a comment marker, so a continuation line like this one
          is searched too. That is how this very comment first turned the grep
          red. */}
      <div className="flex items-center gap-6 text-sm text-[#7A7A8C]">
        <a href="/privacy" data-testid="footer-privacy" className="hover:text-[#C9A84C] transition-colors">Privacy</a>
        <a href="/terms" data-testid="footer-terms" className="hover:text-[#C9A84C] transition-colors">Terms</a>
        <a href={getAppUrl()} className="hover:text-[#C9A84C] transition-colors">Start free</a>
        <a href={getAppUrl()} className="hover:text-[#C9A84C] transition-colors">Open app</a>
      </div>
    </div>
  </footer>
);

export default function Landing() {
  useEffect(() => {
    const prev = document.title;
    const prevScroll = document.documentElement.style.scrollBehavior;
    // Batch 139 — moved with the headline. The tab and the Google result are
    // the first thing a visitor reads, and leaving the old tagline there while
    // the hero argues something else is the same drift one layer down.
    document.title = "SparkCue — a coach that works with you.";
    document.documentElement.style.scrollBehavior = "smooth";
    // Canonical domain (marketing only) — keeps SEO on a single host.
    const link = document.createElement("link");
    link.rel = "canonical";
    link.href = "https://sparkcue.co/";
    document.head.appendChild(link);
    return () => {
      document.title = prev;
      document.documentElement.style.scrollBehavior = prevScroll;
      link.remove();
    };
  }, []);
  return (
    <MotionProvider>
    <div id="top" className="sparkcue-root min-h-screen bg-[#0D0D0F] text-[#F0F0F0] antialiased selection:bg-[#C9A84C]/30 overflow-x-hidden">
      <Nav />
      <main>
        <Hero />
        <Demo />
        <Coaching />
        <Steps />
        <Features />
        <Examples />
        <Emotion />
        <Waitlist />
        <Faq />
        <FinalCta />
      </main>
      <Footer />
      <StickyMobileCta />
    </div>
    </MotionProvider>
  );
}
